import React from 'react';
import { Group, Line, Text, Rect } from 'react-konva';
import { useCanvasStore } from '../../store/canvasStore';
import { useEditorStore } from '../../store/editorStore';
import { THEMES } from '../../floorplanTheme';
import { pixelsToMetres } from '../../canvasConstants';
import type { CanvasShape } from '../../types';

interface DimensionShapeProps {
  shape: CanvasShape;
  onCanvasChange?: () => void;
}

const TICK = 7;

const DimensionShape: React.FC<DimensionShapeProps> = ({ shape, onCanvasChange }) => {
  const { removeShape, pushHistory } = useCanvasStore();
  const { selectedIds, setSelectedIds, activeTool, canvasTheme } = useEditorStore();
  const theme = THEMES[canvasTheme];
  const isSelected = selectedIds.includes(shape.id);
  const pts = shape.points ?? [];
  if (pts.length < 4) return null;

  const [x1, y1, x2, y2] = pts;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy);
  if (len === 0) return null;

  const ux = dx / len;
  const uy = dy / len;
  // Perpendicular unit vector for tick marks.
  const px = -uy;
  const py = ux;

  const color = isSelected ? theme.wall.selectedStroke : canvasTheme === 'light' ? '#1a1a1a' : '#f4f4f8';
  const label = `${pixelsToMetres(len).toFixed(2)} m`;
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;
  const angleDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
  // Keep text upright.
  const textAngle = angleDeg > 90 || angleDeg < -90 ? angleDeg + 180 : angleDeg;

  const handleSelect = () => {
    if (activeTool === 'select') setSelectedIds([shape.id]);
    if (activeTool === 'eraser') {
      removeShape(shape.id);
      pushHistory();
      onCanvasChange?.();
    }
  };

  const approxLabelW = label.length * 9 + 16;

  return (
    <Group
      id={shape.id}
      onClick={handleSelect}
      onTap={handleSelect}
      draggable={activeTool === 'select'}
      onMouseDown={(e) => {
        e.cancelBubble = true;
      }}
      onDragEnd={(e) => {
        const dx = e.target.x();
        const dy = e.target.y();
        e.target.position({ x: 0, y: 0 });
        const { updateShape, pushHistory } = useCanvasStore.getState();
        updateShape(shape.id, {
          points: pts.map((value, index) => value + (index % 2 === 0 ? dx : dy)),
        });
        pushHistory();
        onCanvasChange?.();
      }}
    >
      <Line points={pts} stroke={color} strokeWidth={2} hitStrokeWidth={14} />
      <Line points={[x1 + px * TICK, y1 + py * TICK, x1 - px * TICK, y1 - py * TICK]} stroke={color} strokeWidth={2} listening={false} />
      <Line points={[x2 + px * TICK, y2 + py * TICK, x2 - px * TICK, y2 - py * TICK]} stroke={color} strokeWidth={2} listening={false} />
      <Group x={midX} y={midY} rotation={textAngle} listening={false}>
        <Rect
          x={-approxLabelW / 2}
          y={-22}
          width={approxLabelW}
          height={20}
          fill={theme.canvas.background}
          cornerRadius={3}
        />
        <Text
          x={-approxLabelW / 2}
          y={-20}
          width={approxLabelW}
          align="center"
          text={label}
          fontSize={15}
          fontStyle="bold"
          fontFamily="system-ui, -apple-system, sans-serif"
          fill={color}
          listening={false}
        />
      </Group>
    </Group>
  );
};

export default DimensionShape;
